import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { isDisposableEmail } from 'disposable-email-domains-js';
import { votacaoFase2 } from '../services/Votacao';
import API from '../plugins/axios';

import { useGlobal } from '../contexts/global';
import resolverCidadeSlug from '../utils/cidadeAtiva';
import { getErrorMessage } from '../utils/errorHandler';

import logoGoogle from '../assets/images/logo_google.svg';

import '../assets/scss/components/ModalVotacao.scss';

const tabSP = {
  regiao_central: {
    id: 1,
    nome: 'CENTRO'
  },
  zona_norte: {
    id: 2,
    nome: 'ZONA NORTE'
  },
  zona_leste: {
    id: 3,
    nome: 'ZONA LESTE'
  },
  zona_sul: {
    id: 4,
    nome: 'ZONA SUL'
  },
  zona_oeste: {
    id: 5,
    nome: 'ZONA OESTE'
  },
  melhor_equipe_de_caixa_apresentada_por_mondelez: {
    id: 6,
    nome: 'MELHOR EQUIPE DE CAIXA POR MONDELÉZ'
  },
  guarulhos: {
    id: 7,
    nome: 'PADARIA DE GUARULHOS'
  },
  pao_na_chapa_com_catupiry: {
    id: 8,
    nome: 'PÃO NA CHAPA COM CATUPIRY'
  },
  servico_de_frios_apresentado_por_seara: {
    id: 9,
    nome: 'SERVIÇO DE FRIOS POR SEARA'
  }
};

const tabBH = {
  centro_sul: {
    id: 1,
    nome: 'REGIÃO CENTRO-SUL'
  },
  barreiro_oeste: {
    id: 2,
    nome: 'REGIÃO BARREIRO/OESTE'
  },
  pampulha_noroeste: {
    id: 3,
    nome: 'REGIÃO PAMPULHA/NOROESTE'
  },
  norte_venda_nova: {
    id: 4,
    nome: 'REGIÃO NORTE/VENDA NOVA'
  },
  nordeste_leste: {
    id: 5,
    nome: 'REGIÃO NORDESTE/LESTE'
  }
};

const ModalVotacao = ({ cidade }) => {
  const modal = useRef();
  const [email, setEmail] = useState(localStorage.getItem('user_email') || '');
  const [isLoading, setIsLoading] = useState(false);
  const {
    setShowModalVotacao,
    nomePadaria,
    idPadaria,
    zonaAtual,
    setActiveTabKey
  } = useGlobal();

  const close = (e) => {
    e.stopPropagation();
    setShowModalVotacao(false);
  };

  const handleModal = (e) => {
    if (modal.current !== null && modal.current !== undefined) {
      if (!modal.current.contains(e.target)) {
        close(e);
      }
    }
  };

  const validateEmail = (emailInput) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      return 'Por favor, insira um formato de e-mail válido.';
    }

    if (isDisposableEmail(emailInput)) {
      return 'E-mails temporários não são permitidos nesta votação. Por favor, use um e-mail válido.';
    }

    return null;
  };

  const handleVotacao = (ssoToken = null, social = '') => {
    if (email && !ssoToken) {
      localStorage.setItem('user_email', email);
    }
    setIsLoading(true);
    const grecaptcha = window.grecaptcha?.enterprise || window.grecaptcha;
    grecaptcha.ready(() => {
      grecaptcha
        .execute(process.env.REACT_APP_KEY_RECAPTCHA, {
          action: 'submit'
        })
        .then(async (token) => {
          const data = {
            padaria_id: idPadaria,
            email,
            token_grecaptcha: token
          };

          if (ssoToken) {
            data[social] = true;
          }

          try {
            const resp = await votacaoFase2(data, ssoToken || '');

            const { zones } = resp.data;

            const cidadeSlug = resolverCidadeSlug(
              cidade,
              window.location.pathname
            );
            const tab = cidadeSlug === 'belo-horizonte' ? tabBH : tabSP;

            setShowModalVotacao(false);

            const votosPadarias = zones || [];

            const zonasRestantes = Object.keys(tab).filter(
              (key) => !votosPadarias.includes(tab[key].id)
            );

            let textRedirect = 'Obrigado por votar nesta padaria!';
            let buttonText = 'Voltar';
            let proximaZonaKeyToClick = null;

            if (zonasRestantes.length > 0 && zonasRestantes.length <= 9) {
              const proximaZonaKey = zonasRestantes[0];
              const proximaZona = tab[proximaZonaKey];

              buttonText = `Ir para ${proximaZona.nome}`;
              textRedirect = `Você ainda não votou na zona ${proximaZona.nome}. Clique para votar lá também!`;
              proximaZonaKeyToClick = proximaZonaKey;
            } else {
              textRedirect =
                'Obrigado por votar em todas as padarias! Sua participação é muito importante para nós.';
            }

            Swal.fire({
              icon: ssoToken ? 'success' : 'success',
              title: resp.success || 'Voto computado!',
              text: textRedirect,
              confirmButtonText: buttonText
            }).then(() => {
              setIsLoading(false);

              if (proximaZonaKeyToClick) {
                if (setActiveTabKey) {
                  setActiveTabKey(proximaZonaKeyToClick);
                }

                setTimeout(() => {
                  const targetElement =
                    document.getElementById('padarias') ||
                    document.getElementById('section-votacao');

                  if (targetElement) {
                    const y =
                      targetElement.getBoundingClientRect().top +
                      window.pageYOffset -
                      20;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }

                  const normKey = (proximaZonaKeyToClick || '')
                    .toLowerCase()
                    .replace(/[-_\s]+/g, '');

                  const tabs = document.querySelectorAll('[data-tab-key]');
                  tabs.forEach((tabEl) => {
                    const k = (tabEl.getAttribute('data-tab-key') || '')
                      .toLowerCase()
                      .replace(/[-_\s]+/g, '');
                    if (k === normKey) {
                      tabEl.click();
                    }
                  });
                }, 300);
              }
            });
          } catch (error) {
            setShowModalVotacao(false);

            if (ssoToken && error.response?.status === 401) {
              sessionStorage.removeItem(`${social}_access_token`);
              // eslint-disable-next-line no-use-before-define
              handleClickSSO(social, true);
              return;
            }

            if (error.response?.status === 422) {
              const errorMsg = getErrorMessage(
                error,
                'Usuário já votou nessa categoria!'
              );
              Swal.fire({
                timer: 3500,
                icon: 'error',
                showConfirmButton: false,
                text: errorMsg
              });
            } else {
              const errorMsg = getErrorMessage(
                error,
                'Ops! Houve um erro, tente novamente mais tarde.'
              );
              Swal.fire({
                timer: 3500,
                icon: 'error',
                showConfirmButton: false,
                text: errorMsg
              });
            }
            setIsLoading(false);
          }
        });
    });
  };

  const handleClickSSO = (social, forceRedirect = false) => {
    const data = {
      padaria_id: idPadaria
    };

    const savedToken = sessionStorage.getItem(`${social}_access_token`);

    if (savedToken && !forceRedirect) {
      handleVotacao(savedToken, social);
      return;
    }

    localStorage.setItem('vote_data', JSON.stringify(data));
    localStorage.setItem('last_zone', JSON.stringify(zonaAtual));

    if (social === 'google') {
      window.location.href = `${API.defaults.baseURL}auth/login/google`;
    } else if (social === 'facebook') {
      window.location.href = `${API.defaults.baseURL}auth/login/facebook`;
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleModal);
    return () => {
      document.removeEventListener('mousedown', handleModal);
    };
  }, []);

  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = () => {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_KEY_RECAPTCHA}`,
      () => {}
    );
  }, []);

  return (
    <div className="in-vote">
      <div className="in-vote-modal" ref={modal}>
        <div className="in-vote-modal-grid">
          <div className="in-vote-modal-grid-title">
            <p>Confirmar voto em:</p>
            <p>{nomePadaria}</p>
            <p>
              Para finalizar o voto precisamos fazer uma
              <br />
              confirmação por e-mail
            </p>
          </div>
          <div className="in-vote-modal-grid-email">
            <div className="in-vote-modal-grid-email-input">
              <input
                id="email"
                type="email"
                name="email"
                value={email}
                placeholder="Digite o seu e-mail"
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                disabled={email === '' || isLoading}
                type="button"
                onClick={() => {
                  const errorMessage = validateEmail(email);

                  if (errorMessage) {
                    Swal.fire({
                      icon: 'error',
                      title: 'E-mail inválido',
                      text: errorMessage,
                      confirmButtonText: 'Entendi'
                    });
                    return;
                  }

                  handleVotacao();
                }}
              >
                {isLoading ? (
                  <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>

            <div className="in-vote-modal-grid-email-or">
              <div />
              <span>ou</span>
              <div />
            </div>

            <div className="in-vote-modal-grid-email-btn">
              <button type="button" onClick={() => handleClickSSO('google')}>
                <img src={logoGoogle} alt="Google logo" />
                <span>Confirmar voto com o Google</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ModalVotacao.propTypes = {
  cidade: PropTypes.shape({
    slug: PropTypes.string,
    pagina: PropTypes.string
  })
};

ModalVotacao.defaultProps = {
  cidade: null
};

export default ModalVotacao;
